import { useState, useEffect } from 'react';
import { sortArray } from '../utils';

/**
 * Hook for managing table data - filtering and sorting
 * @param {Object} options - Configuration options
 * @param {Array} options.data - Raw data array
 * @param {string} options.search - Search string
 * @param {Object} options.sortConfig - Sort configuration { order, reverse }
 * @param {Array} options.columnsState - Columns with visibility state
 * @returns {Object} Filtered and sorted view data
 */
const useTableData = ({ data, search, sortConfig, columnsState }) => {
  const [dataArray, setDataArray] = useState([]);
  const [viewArray, setViewArray] = useState([]);

  // Update data when prop changes
  useEffect(() => {
    setDataArray(Array.isArray(data) ? [...data] : data);
    setViewArray(Array.isArray(data) ? [...data] : data);
  }, [data]);

  // Apply search and sort when data, search, or sort changes
  useEffect(() => {
    let filtered = [...dataArray];

    // Apply search
    if (search) {
      const columnsToSearch = columnsState
        .map((col, index) => {
          if (index === 0 || col.search) {
            return [col.id, col.displayValue].filter(Boolean);
          }
          return null;
        })
        .filter(Boolean)
        .flat();

      const searchStr = search.toLowerCase();
      filtered = filtered.filter((row) =>
        columnsToSearch.some((colId) => {
          const val = String(row[colId] || '').toLowerCase();
          return val.indexOf(searchStr) > -1;
        })
      );
    }

    // Apply sort
    const sorted = sortArray(filtered, sortConfig);
    setViewArray(sorted);
  }, [dataArray, search, sortConfig, columnsState]);

  return {
    dataArray,
    viewArray,
  };
};

export default useTableData;
